import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraButton, SpecraInlineBanner } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useRef, useState } from 'react';
import { fiscalQuarterDates, fiscalQuarterLabel, parseFiscalQuarterLabel } from './fiscalQuarter';
const QUARTERS = [
    1,
    2,
    3,
    4
];
// A few contract years back and a few forward — covers reviewing a past
// year's scores and planning next year's without an unbounded list. Recomputed
// from the real clock each render (this is ordinary app code, not a fixed
// lookup table), so the range keeps drifting forward with the calendar
// instead of going stale.
function fiscalYearOptions() {
    const currentYear = new Date().getFullYear();
    return Array.from({
        length: 9
    }, (_, i)=>currentYear - 3 + i);
}
/**
 * Fiscal-year + quarter picker for QASP scores (SAD-174). Composes the
 * `YYYY-Qn` quarter label and derives the federal-fiscal-year start/end dates
 * (see fiscalQuarter.ts) into the host form's own fields via `form.setValue` —
 * used identically from the create dialog's `extraContent` (EntityFormAccess)
 * and the detail drawer's `children` (ScoreDetailFormAccess), both of which
 * expose the same watch/getValues/setValue shape.
 *
 * Derivation only ever runs from this component's own onValueChange handlers
 * (a real pick), never from a `useEffect` watching the field — so a
 * programmatic `setValue` from elsewhere (ProposalReview's onApplyToDraft
 * applying an accepted quarter/period-start/period-end proposal) updates the
 * displayed selection but never re-triggers a derivation that would clobber
 * an independently-applied period proposal.
 */ export function FiscalQuarterPicker({ form, quarterField, startField, endField, disabled }) {
    // A pick that would overwrite a manually-adjusted date waits for explicit
    // confirmation instead of silently applying — this holds the pending
    // selection while that notice is shown.
    const [pendingReset, setPendingReset] = useState(null);
    // The dates THIS component last wrote (by a pick or a confirmed reset), so
    // a follow-up pick (e.g. choosing the year, then the quarter) recognizes
    // its own prior write as safe to replace — rather than mistaking its own
    // intermediate derivation for a manual edit worth protecting.
    const [lastDerived, setLastDerived] = useState(null);
    // The banner renders below whatever the user was just looking at (the
    // quarter select, often near the bottom of a scrolled form/drawer) — with
    // nothing to bring it into view, the explanation for why their manual edit
    // was protected can land off-screen and go unseen.
    const resetNoticeRef = useRef(null);
    useEffect(()=>{
        if (pendingReset) resetNoticeRef.current?.scrollIntoView({
            block: 'nearest'
        });
    }, [
        pendingReset
    ]);
    const currentLabel = String(form.watch(quarterField) ?? '');
    const parsed = parseFiscalQuarterLabel(currentLabel);
    const fiscalYear = parsed === 'unparseable' ? undefined : parsed.fiscalYear;
    const quarter = parsed === 'unparseable' ? undefined : parsed.quarter;
    const applySelection = (fy, q)=>{
        form.setValue(quarterField, fiscalQuarterLabel(fy, q));
        const dates = fiscalQuarterDates(fy, q);
        const curStart = String(form.getValues(startField) ?? '');
        const curEnd = String(form.getValues(endField) ?? '');
        // No manual adjustment to protect when the dates are: untouched (blank),
        // already exactly what this pick would derive (re-picking the same
        // quarter), or exactly what THIS component last derived (nothing manual
        // happened since — including a same-render FY-then-quarter pick chain).
        const matches = (d)=>!!d && curStart === d.start && curEnd === d.end;
        const noAdjustmentToProtect = curStart === '' && curEnd === '' || matches(dates) || matches(lastDerived);
        if (noAdjustmentToProtect) {
            form.setValue(startField, dates.start);
            form.setValue(endField, dates.end);
            setLastDerived(dates);
            setPendingReset(null);
        } else {
            setPendingReset({
                fiscalYear: fy,
                quarter: q
            });
        }
    };
    const confirmReset = ()=>{
        if (!pendingReset) return;
        const dates = fiscalQuarterDates(pendingReset.fiscalYear, pendingReset.quarter);
        form.setValue(startField, dates.start);
        form.setValue(endField, dates.end);
        setLastDerived(dates);
        setPendingReset(null);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex gap-2",
                children: [
                    /*#__PURE__*/ _jsxs(Select, {
                        value: fiscalYear != null ? String(fiscalYear) : '',
                        onValueChange: (v)=>applySelection(Number(v), quarter ?? 2),
                        disabled: disabled,
                        children: [
                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                "data-testid": Testids.deliveryQuality.qaspFiscalYearSelect,
                                children: /*#__PURE__*/ _jsx(SelectValue, {
                                    placeholder: "Fiscal year"
                                })
                            }),
                            /*#__PURE__*/ _jsx(SelectContent, {
                                children: fiscalYearOptions().map((fy)=>/*#__PURE__*/ _jsxs(SelectItem, {
                                        value: String(fy),
                                        children: [
                                            "FY",
                                            fy
                                        ]
                                    }, fy))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Select, {
                        value: quarter != null ? String(quarter) : '',
                        onValueChange: (v)=>applySelection(fiscalYear ?? new Date().getFullYear(), Number(v)),
                        disabled: disabled,
                        children: [
                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                "data-testid": Testids.deliveryQuality.qaspQuarterSelect,
                                children: /*#__PURE__*/ _jsx(SelectValue, {
                                    placeholder: "Quarter"
                                })
                            }),
                            /*#__PURE__*/ _jsx(SelectContent, {
                                children: QUARTERS.map((q)=>/*#__PURE__*/ _jsxs(SelectItem, {
                                        value: String(q),
                                        children: [
                                            "Q",
                                            q
                                        ]
                                    }, q))
                            })
                        ]
                    })
                ]
            }),
            parsed === 'unparseable' && currentLabel && /*#__PURE__*/ _jsxs("p", {
                className: "text-xs text-muted-foreground",
                children: [
                    "Current quarter “",
                    currentLabel,
                    "” isn't a recognized fiscal quarter — it will be kept as-is unless you pick one above to replace it."
                ]
            }),
            pendingReset && /*#__PURE__*/ _jsx("div", {
                ref: resetNoticeRef,
                children: /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "warning",
                    "data-testid": Testids.deliveryQuality.qaspQuarterResetNotice,
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap items-center justify-between gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                children: "The period start/end were adjusted manually, so changing the quarter didn't touch them."
                            }),
                            /*#__PURE__*/ _jsxs(SpecraButton, {
                                type: "button",
                                size: "sm",
                                variant: "secondary",
                                onClick: confirmReset,
                                children: [
                                    "Reset dates to ",
                                    fiscalQuarterLabel(pendingReset.fiscalYear, pendingReset.quarter),
                                    ' ',
                                    "default"
                                ]
                            })
                        ]
                    })
                })
            })
        ]
    });
}
